import type React from 'react';
import { type DependencyList, type MutableRefObject, useCallback, useRef } from 'react';
import isClient from 'helpers/isClient';
import { debounce } from 'lodash-es';
import useIsomorphicLayoutEffect from './useIsomorphicLayoutEffect';

interface IPosition {
    x: number;
    y: number;
}

interface IScrollProps {
    prevPos: IPosition;
    currPos: IPosition;
}

const zeroPosition = { x: 0, y: 0 };

const getScrollPosition = <T extends HTMLElement = HTMLDivElement>(element: React.MutableRefObject<T | undefined>) => {
    if (!isClient) {
        return zeroPosition;
    }

    const containerPosition = {
        x: element?.current?.scrollLeft || zeroPosition.x,
        y: element?.current?.scrollTop || zeroPosition.y
    };

    return containerPosition;
};

/**
 * useScrollPositionEffect fires the effectCallback function each time a scroll effect occurs.
 * State can be manipulated in the effectCallback since the effectCallback will receive the scroll position as argument.
 * @param effectCallback The effect callback that will be called with the scroll position as arguments.
 * @param deps dependencies
 * @param wait The specified debounce time (defaults to 250ms for performance reasons)
 * @returns MutableRefObject
 */
export default function useScrollPositionEffect<T extends HTMLElement = HTMLDivElement>(
    effectCallback: (props: IScrollProps) => void,
    deps: DependencyList,
    wait?: number
): [MutableRefObject<T | undefined>] {
    const ref = useRef<T | undefined>();
    const position = useRef(getScrollPosition(ref));

    const update = () => {
        const currPos = getScrollPosition(ref);
        effectCallback({ prevPos: position.current, currPos });
        position.current = currPos;
    };

    const callback = useCallback(wait ? debounce(() => update(), wait) : () => update(), []);

    useIsomorphicLayoutEffect(() => {
        if (!isClient) {
            return undefined;
        }

        if (ref) {
            ref.current?.addEventListener('scroll', callback, { passive: true });
        }

        return () => {
            if (ref) {
                ref.current?.removeEventListener('scroll', callback);
            }
        };
    }, deps);

    return [ref];
}
